import type {
  IAuthenticationService,
  ITokenService,
  IUserStorageService,
} from "@/application/ports/services";
import type { ILogger } from "@/application/ports/ILogger";
import type { IMonitoring } from "@/application/ports/IMonitoring";
import type { AuthState } from "@/domain/types";
import { UserEntity } from "@/domain/user";
import { getUserErrorMessage } from "@/application/errors/ApiClientError";
import { toSentryContext, clearAuthData } from "@/application/services/authUtils";

export class ValidateAuthStatusUseCase {
  private authService: IAuthenticationService;
  private tokenService: ITokenService;
  private userStorageService: IUserStorageService;
  private logger: ILogger;
  private monitoring: IMonitoring;

  constructor(
    authService: IAuthenticationService,
    tokenService: ITokenService,
    userStorageService: IUserStorageService,
    logger: ILogger,
    monitoring: IMonitoring,
  ) {
    this.authService = authService;
    this.tokenService = tokenService;
    this.userStorageService = userStorageService;
    this.logger = logger;
    this.monitoring = monitoring;
  }

  async execute(): Promise<AuthState> {
    try {
      // Check if we have a stored token
      const token = this.tokenService.getToken();

      if (!token) {
        this.clearAuth();
        return {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        };
      }

      // Check if token is expired and try to refresh if needed
      if (this.tokenService.isTokenExpired(token)) {
        const refreshToken = this.tokenService.getRefreshToken();

        if (refreshToken) {
          // Attempt to refresh the token
          const refreshResult = await this.authService.refreshToken();

          if (refreshResult.success && refreshResult.token && refreshResult.user) {
            // Update stored tokens
            this.tokenService.setToken(refreshResult.token);
            if (refreshResult.refreshToken) {
              this.tokenService.setRefreshToken(refreshResult.refreshToken);
            }
            this.userStorageService.setCurrentUser(refreshResult.user);

            // Set Sentry user context after successful refresh
            this.monitoring.setUserContext(toSentryContext(refreshResult.user, null));

            return {
              user: refreshResult.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            };
          }
        }

        // If refresh failed or no refresh token available
        this.clearAuth();
        return {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: "Session expired",
        };
      }

      // Validate token with the authentication service
      const validationResult = await this.authService.validateToken(token);

      if (!validationResult.isValid || !validationResult.user) {
        // If the failure is transient (500, network error), attempt a refresh
        // before giving up — the token may still be valid
        if (validationResult.errorCode === "transient_error") {
          const refreshToken = this.tokenService.getRefreshToken();
          if (refreshToken) {
            const refreshResult = await this.authService.refreshToken();
            if (refreshResult.success && refreshResult.token && refreshResult.user) {
              this.tokenService.setToken(refreshResult.token);
              if (refreshResult.refreshToken) {
                this.tokenService.setRefreshToken(refreshResult.refreshToken);
              }
              this.userStorageService.setCurrentUser(refreshResult.user);
              this.monitoring.setUserContext(toSentryContext(refreshResult.user, null));

              return {
                user: refreshResult.user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              };
            }
          }
        }

        this.clearAuth();
        return {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: validationResult.error || "Invalid authentication",
        };
      }

      // Create user entity for business logic validation
      const userEntity = UserEntity.create(validationResult.user);

      // Check if user can still login (account might have been deactivated)
      if (!userEntity.canLogin()) {
        this.clearAuth();
        return {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: "Account is no longer active",
        };
      }

      // Update stored user data (in case it changed on the server)
      this.userStorageService.setCurrentUser(validationResult.user);

      // Set Sentry user context after successful validation
      this.monitoring.setUserContext(toSentryContext(validationResult.user, null));

      return {
        user: validationResult.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    } catch (error) {
      this.logger.error("Auth validation failed:", error);
      this.clearAuth();

      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: getUserErrorMessage(error, "Authentication check failed"),
      };
    }
  }

  private clearAuth(): void {
    clearAuthData(this.tokenService, this.userStorageService, this.monitoring);
  }
}
